import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, memo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  CircularProgress,
} from '@mui/material';

import styled from '@emotion/styled';
import {
  distanceToLineSegment,
  isNearPolygonEdgesOrVertices,
  VERTEX_HIT_RADIUS,
  EDGE_HIT_THRESHOLD,
  SAM_UNLABELED_COLOR,
  segmentsToSamPreviewPolygons,
  isUnlabeledPolygon,
  mapPointFromImageSpace,
  mapPolygonsToImageSpace,
  type CoordRemapMode,
} from './helpers';
import {
  checkSamLabellingStatus,
  initiateSam,
  performSam,
} from '@services/samService';
import {
  showErrorSnackbar,
  showSuccessSnacbar,
} from '@components/shared-layouts/Snackbar/helper';

const SAM_POLL_INTERVAL_MS = 30000;



interface Point {
  x: number;
  y: number;
}

interface Polygon {
  points: Point[];
  label: string;
  color: string;
}

interface ImageData {
  id: string;
  url: string;
  width: number;
  height: number;
}

interface Label {
  name: string;
  color: string;
}

const EMPTY_LABELS: Label[] = [];

interface SegmentationData {
  [imageId: string]: {
    polygons: Polygon[];
    activePolygon: Point[];
  };
}

interface ImageSizes {
  size: number[];
  targetSize: number[];
}

interface ImageSegmentationViewerProps {
  imageData: ImageData;
  projectId?: string | number;
  imageId?: string;
  labelsFromContext?: Label[];
  preLoadedAnnotations?: Polygon[];
  segmentationData: SegmentationData;
  setSegmentationData: React.Dispatch<React.SetStateAction<SegmentationData>>;
  imageSizes?: ImageSizes | null;
  coordRemapMode?: CoordRemapMode;
}

const ViewerContainer = styled(Paper)`
  padding: 16px;
  margin-top: 16px;
  position: relative;
  background: #fff;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ControlsContainer = styled(Box)`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  gap: 24px;
`;

const ViewerWrapper = styled(Box)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CanvasContainer = styled(Box)`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  position: relative;

  canvas {
    max-width: 100%;
    max-height: 100%;
    width: auto !important;
    height: auto !important;
    object-fit: contain;
  }
`;

const WelcomeContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100% !important;
  padding: 20px;
  text-align: center;
  color: #666;
  position: relative;
`;

const WelcomeContent = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  max-width: 600px;
  margin: 0 auto;
`;

const WelcomeIcon = styled.div`
  font-size: 48px;
  color: #f04a23;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    display: block;
    width: 64px;
    height: 64px;
    background: #f04a23;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z'/%3E%3C/svg%3E") center/contain no-repeat;
  }
`;

const WelcomeTitle = styled(Typography)`
  color: #f04a23;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const WelcomeText = styled(Typography)`
  font-size: 16px;
  line-height: 1.6;
  color: #666;
  max-width: 500px;
  text-align: center;
`;

function ImageSegmentationViewer({ 
  imageData,
  projectId,
  imageId, 
  labelsFromContext, 
  preLoadedAnnotations,
  segmentationData,
  setSegmentationData,
  imageSizes,
  coordRemapMode = 'none',
}: ImageSegmentationViewerProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeLabel, setActiveLabel] = useState<string>('');
  const labels = labelsFromContext ?? EMPTY_LABELS;
  const [selectedPolygon, setSelectedPolygon] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPolygonClosed, setIsPolygonClosed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPoint, setDragPoint] = useState<{ index: number, initialPos: Point } | null>(null);
  const [pendingPolygon, setPendingPolygon] = useState<Point[] | null>(null);
  const [editingLabel, setEditingLabel] = useState<{ polygonIndex: number, position: { x: number, y: number } } | null>(null);
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);

  const [enableSam, setEnableSam] = useState(false);
  const [samToggleLoading, setSamToggleLoading] = useState(false);
  const [samPreviewPolygons, setSamPreviewPolygons] = useState<Polygon[]>([]);
  const [samLoading, setSamLoading] = useState(false);
  const [samInitDialogOpen, setSamInitDialogOpen] = useState(false);
  const [samInitPercentage, setSamInitPercentage] = useState(0);
  const [samInitMessage, setSamInitMessage] = useState(
    'We are initializing SAM for this project. Please wait.'
  );
  const [labelQueueIndices, setLabelQueueIndices] = useState<number[]>([]);
  const [labelQueuePosition, setLabelQueuePosition] = useState(0);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const segmentationCanvasRef = useRef<HTMLCanvasElement>(null);
  const lastCursorPosition = useRef<Point | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const samPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Suppress edit-mode exit on click that follows vertex drag / edge insert */
  const suppressEditModeExitClickRef = useRef(false);

  const clearSamPollInterval = useCallback(() => {
    if (samPollIntervalRef.current) {
      clearInterval(samPollIntervalRef.current);
      samPollIntervalRef.current = null;
    }
  }, []);

  const openSamInitDialog = useCallback((message?: string, percentage?: number) => {
    setSamInitMessage(
      message ?? 'We are initializing SAM for this project. Please wait.'
    );
    setSamInitPercentage(percentage ?? 0);
    setSamInitDialogOpen(true);
  }, []);

  const closeSamInitDialog = useCallback(() => {
    clearSamPollInterval();
    setSamInitDialogOpen(false);
  }, [clearSamPollInterval]);

  const pollSamInitStatus = useCallback(async () => {
    if (!projectId) return;

    try {
      const result = await checkSamLabellingStatus(projectId);
      if (result.status !== 200 || !result.data) return;

      const { message, percentage } = result.data;
      setSamInitMessage(message);
      setSamInitPercentage(percentage);

      if (percentage >= 100) {
        closeSamInitDialog();
        showSuccessSnacbar('SAM is ready. Turn on Enable SAM to use it.');
      }
    } catch (err) {
      console.error('SAM status poll error:', err);
    }
  }, [projectId, closeSamInitDialog]);

  useEffect(() => {
    if (!samInitDialogOpen || !projectId) {
      clearSamPollInterval();
      return;
    }

    pollSamInitStatus();
    samPollIntervalRef.current = setInterval(pollSamInitStatus, SAM_POLL_INTERVAL_MS);

    return () => clearSamPollInterval();
  }, [samInitDialogOpen, projectId, pollSamInitStatus, clearSamPollInterval]);

  useEffect(() => {
    setSamPreviewPolygons([]);
  }, [imageId]);

  const clearDrawingState = useCallback(() => {
    setIsEditing(false);
    setSelectedPolygon(null);
    setPendingPolygon(null);
    setIsPolygonClosed(false);
    setEditingLabel(null);
    if (imageId) {
      setSegmentationData((prev) => ({
        ...prev,
        [imageId]: {
          ...prev[imageId],
          activePolygon: [],
        },
      }));
    }
  }, [imageId, setSegmentationData]);

  const handleSamToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!projectId || samToggleLoading) return;

    if (!checked) {
      if (!imageId) {
        setEnableSam(false);
        setSamPreviewPolygons([]);
        return;
      }

      const currentPolygons = segmentationData[imageId]?.polygons ?? [];
      const mergedPolygons = [...currentPolygons, ...samPreviewPolygons];

      setSegmentationData((prev) => ({
        ...prev,
        [imageId]: {
          ...prev[imageId],
          polygons: mergedPolygons,
          activePolygon: [],
        },
      }));

      setSamPreviewPolygons([]);
      setEnableSam(false);

      const unlabeledIndices = mergedPolygons
        .map((p, i) => (isUnlabeledPolygon(p) ? i : -1))
        .filter((i) => i >= 0);

      if (unlabeledIndices.length > 0) {
        setLabelQueueIndices(unlabeledIndices);
        setLabelQueuePosition(0);
      }
      return;
    }

    setSamToggleLoading(true);
    try {
      const result = await checkSamLabellingStatus(projectId);

      if (result.status === 200 && result.data) {
        const { message, percentage } = result.data;
        if (percentage >= 100) {
          clearDrawingState();
          setEnableSam(true);
        } else {
          openSamInitDialog(message, percentage);
        }
      } else if (result.status === 400) {
        await initiateSam(projectId);
        openSamInitDialog();
      }
    } catch (err) {
      console.error('SAM enable error:', err);
      const errorMessage =
        (err as { error?: { message?: string } })?.error?.message ||
        'Failed to enable SAM';
      showErrorSnackbar(errorMessage);
    } finally {
      setSamToggleLoading(false);
    }
  };

  const handleSamInitDismiss = () => {
    closeSamInitDialog();
  };

  const performSamSegmentation = useCallback(
    async (canvasCoords: Point) => {
      if (!projectId || !imageId || !imageSizes || samLoading) return;

      const { size, targetSize } = imageSizes;
      const apiPoint = mapPointFromImageSpace(
        canvasCoords,
        size,
        targetSize,
        coordRemapMode
      );

      setSamLoading(true);
      try {
        const response = await performSam(
          projectId,
          imageId,
          Math.round(apiPoint.x),
          Math.round(apiPoint.y)
        );

        const rawPolygons = segmentsToSamPreviewPolygons(response.segments);
        if (!rawPolygons.length) {
          showErrorSnackbar('SAM did not return valid segments');
          return;
        }

        const imagePolygons = mapPolygonsToImageSpace(
          rawPolygons,
          size,
          targetSize,
          coordRemapMode
        );

        setSamPreviewPolygons((prev) => [...prev, ...imagePolygons]);
      } catch (err) {
        console.error('perform-sam error:', err);
        const errorMessage =
          (err as { error?: { message?: string } })?.error?.message ||
          'Failed to perform SAM segmentation';
        showErrorSnackbar(errorMessage);
      } finally {
        setSamLoading(false);
      }
    },
    [projectId, imageId, imageSizes, coordRemapMode, samLoading]
  );

  // Load image
  useEffect(() => {
    if (imageData?.url) {
      setImageLoaded(false);
      setLoadedImage(null);

      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        setImageLoaded(true);
        setError('');
        setLoadedImage(img);
      };
      
      img.onerror = (e) => {
        console.error('Image load error:', e);
        setError('Failed to load image');
        setImageLoaded(false);
        setLoadedImage(null);
      };
      
      img.src = imageData.url;
    }
  }, [imageData]);

  // Initialize segmentation data for current image
  useEffect(() => {
    if (imageId && !segmentationData[imageId]) {
      setSegmentationData(prev => ({
        ...prev,
        [imageId]: {
          polygons: preLoadedAnnotations || [],
          activePolygon: []
        }
      }));
    }
  }, [imageId, segmentationData, preLoadedAnnotations, setSegmentationData]);

  // Render image on canvas
  const renderImage = useCallback(() => {
    if (!canvasRef.current || !imageLoaded || !loadedImage) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions to match image
    canvas.width = loadedImage.naturalWidth;
    canvas.height = loadedImage.naturalHeight;

    // Clear canvas and draw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(loadedImage, 0, 0);
  }, [imageLoaded, loadedImage]);

  // Render segmentation overlays
  const renderSegmentation = useCallback(() => {
    if (
      !segmentationCanvasRef.current ||
      !canvasRef.current ||
      !imageId ||
      !imageLoaded ||
      !loadedImage
    ) {
      return;
    }

    const ctx = segmentationCanvasRef.current.getContext('2d');
    if (!ctx) return;

    // Ensure image canvas is sized before drawing overlays
    if (canvasRef.current.width === 0 || canvasRef.current.height === 0) {
      return;
    }

    // Make sure segmentation canvas matches the image canvas dimensions
    segmentationCanvasRef.current.width = canvasRef.current.width;
    segmentationCanvasRef.current.height = canvasRef.current.height;

    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    const currentData = segmentationData[imageId];
    if (!currentData) return;

    // Draw completed polygons
    currentData.polygons.forEach((polygon, index) => {
      if (polygon.points.length < 2) return;
      
      // Draw filled polygon
      ctx.beginPath();
      ctx.moveTo(polygon.points[0].x, polygon.points[0].y);
      polygon.points.forEach((point, i) => {
        if (i > 0) ctx.lineTo(point.x, point.y);
      });
      ctx.closePath();
      
      const isSelected = !enableSam && index === selectedPolygon && isEditing;
      ctx.fillStyle = isSelected ? `${polygon.color}40` : `${polygon.color}20`;
      ctx.strokeStyle = isSelected ? '#FFFFFF' : polygon.color;
      ctx.lineWidth = isSelected ? 3 : 1.5;
      
      ctx.fill();
      ctx.stroke();

      // Draw vertices for all polygons (not in SAM mode)
      if (!enableSam) {
        polygon.points.forEach((point) => {
          ctx.beginPath();
          ctx.arc(
            point.x, 
            point.y, 
            isSelected ? 5 : 3,
            0, 
            2 * Math.PI
          );
          ctx.fillStyle = isSelected ? '#FFFFFF' : polygon.color;
          ctx.strokeStyle = polygon.color;
          ctx.lineWidth = isSelected ? 2 : 1;
          ctx.fill();
          ctx.stroke();
        });
      }
    });

    // Draw SAM preview polygons
    samPreviewPolygons.forEach((polygon) => {
      if (polygon.points.length < 2) return;

      ctx.beginPath();
      ctx.moveTo(polygon.points[0].x, polygon.points[0].y);
      polygon.points.forEach((point, i) => {
        if (i > 0) ctx.lineTo(point.x, point.y);
      });
      ctx.closePath();

      ctx.fillStyle = `${SAM_UNLABELED_COLOR}35`;
      ctx.strokeStyle = SAM_UNLABELED_COLOR;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.fill();
      ctx.stroke();
      ctx.setLineDash([]);
    });

    // Draw active polygon
    if (!enableSam && currentData.activePolygon.length > 0) {
      const labelColor = labels.find(l => l.name === activeLabel)?.color || '#FF0000';
      
      // Draw lines between points
      ctx.beginPath();
      ctx.moveTo(currentData.activePolygon[0].x, currentData.activePolygon[0].y);
      currentData.activePolygon.forEach((point, i) => {
        if (i > 0) ctx.lineTo(point.x, point.y);
      });
      
      // If polygon is closed or near first point, close the path
      if (isPolygonClosed || (lastCursorPosition.current && currentData.activePolygon.length > 2)) {
        const firstPoint = currentData.activePolygon[0];
        if (lastCursorPosition.current) {
          const distance = Math.hypot(
            firstPoint.x - lastCursorPosition.current.x,
            firstPoint.y - lastCursorPosition.current.y
          );
          
          if (distance < 10) {
            ctx.lineTo(firstPoint.x, firstPoint.y);
          } else {
            ctx.lineTo(lastCursorPosition.current.x, lastCursorPosition.current.y);
          }
        }
      }
      
      ctx.strokeStyle = labelColor;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw vertices for active polygon
      currentData.activePolygon.forEach((point, index) => {
        ctx.beginPath();
        ctx.arc(
          point.x, 
          point.y, 
          index === 0 ? 6 : 4,
          0, 
          2 * Math.PI
        );
        ctx.fillStyle = index === 0 ? '#FFFFFF' : labelColor;
        ctx.strokeStyle = labelColor;
        ctx.lineWidth = index === 0 ? 2 : 1.5;
        ctx.fill();
        ctx.stroke();
      });
    }
  }, [
    segmentationData,
    imageId,
    selectedPolygon,
    isEditing,
    activeLabel,
    isPolygonClosed,
    labels,
    imageLoaded,
    loadedImage,
    enableSam,
    samPreviewPolygons,
  ]);

  // Paint image + overlays synchronously when data or selection state changes
  useLayoutEffect(() => {
    if (!imageLoaded || !loadedImage) return;
    renderImage();
    renderSegmentation();
  }, [
    imageLoaded,
    loadedImage,
    segmentationData,
    imageId,
    preLoadedAnnotations,
    isEditing,
    selectedPolygon,
    isDragging,
    isPolygonClosed,
    activeLabel,
    enableSam,
    samPreviewPolygons,
    renderImage,
    renderSegmentation,
  ]);

  // Get canvas coordinates
  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!segmentationCanvasRef.current) return null;
    
    const canvas = segmentationCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    return { x, y };
  };

  // Check if point is inside polygon
  const isPointInPolygon = (point: Point, polygon: Point[]) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      const intersect = ((yi > point.y) !== (yj > point.y))
          && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Handle canvas click
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoordinates(e);
    if (!coords || !imageId) return;

    const currentData = segmentationData[imageId];
    if (!currentData) return;

    if (enableSam) {
      performSamSegmentation(coords);
      return;
    }

    // If in edit mode, exit only on click outside polygon and away from edges/vertices
    if (isEditing && selectedPolygon !== null) {
      if (suppressEditModeExitClickRef.current) {
        suppressEditModeExitClickRef.current = false;
        return;
      }

      const currentPolygon = currentData.polygons[selectedPolygon].points;
      const staysInEdit =
        isPointInPolygon(coords, currentPolygon) ||
        isNearPolygonEdgesOrVertices(coords, currentPolygon);

      if (!staysInEdit) {
        setIsEditing(false);
        setSelectedPolygon(null);
      }
      return;
    }

    // Only proceed with polygon selection or drawing if not in edit mode
    if (!isEditing) {
      // Check if clicked inside any completed polygon
      for (let i = currentData.polygons.length - 1; i >= 0; i--) {
        if (isPointInPolygon(coords, currentData.polygons[i].points)) {
          setSelectedPolygon(i);
          setIsEditing(true);
          return;
        }
      }

      // Handle polygon drawing only if not in edit mode
      if (currentData.activePolygon.length > 2) {
        const firstPoint = currentData.activePolygon[0];
        const distance = Math.hypot(firstPoint.x - coords.x, firstPoint.y - coords.y);
        
        if (distance < 10) {
          setPendingPolygon([...currentData.activePolygon]);
          return;
        }
      }

      // Continue drawing
      setSegmentationData(prev => ({
        ...prev,
        [imageId]: {
          ...prev[imageId],
          activePolygon: [...(prev[imageId]?.activePolygon || []), coords]
        }
      }));
    }
  }, [segmentationData, imageId, isEditing, selectedPolygon, setSegmentationData, enableSam, performSamSegmentation]);

  // Handle mouse move
  const handleSegmentationCanvasMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoordinates(e);
    if (!coords || !segmentationCanvasRef.current || !imageId) return;

    const canvas = segmentationCanvasRef.current;
    lastCursorPosition.current = coords;

    if (enableSam) {
      canvas.style.cursor = samLoading ? 'wait' : 'crosshair';
      canvas.title = samLoading ? 'Segmenting...' : 'Click to segment with SAM';
      renderSegmentation();
      return;
    }

    // Handle point dragging
    if (isDragging && dragPoint !== null && selectedPolygon !== null) {
      setSegmentationData(prev => {
        const currentData = prev[imageId];
        const newPolygons = [...currentData.polygons];
        const updatedPoints = [...newPolygons[selectedPolygon].points];
        updatedPoints[dragPoint.index] = coords;
        newPolygons[selectedPolygon] = {
          ...newPolygons[selectedPolygon],
          points: updatedPoints
        };

        return {
          ...prev,
          [imageId]: {
            ...currentData,
            polygons: newPolygons
          }
        };
      });
      return;
    }

    // Handle cursor style changes
    const currentData = segmentationData[imageId];

    // Edit mode point dragging/removal
    if (isEditing && selectedPolygon !== null && currentData?.polygons[selectedPolygon]) {
      const points = currentData.polygons[selectedPolygon].points;

      const isNearPoint = points.some((point: Point) =>
        Math.hypot(point.x - coords.x, point.y - coords.y) < VERTEX_HIT_RADIUS
      );

      let isNearLine = false;
      if (!isNearPoint) {
        for (let i = 0; i < points.length; i++) {
          const start = points[i];
          const end = points[(i + 1) % points.length];
          const { distance } = distanceToLineSegment(coords, start, end);
          if (distance < EDGE_HIT_THRESHOLD) {
            isNearLine = true;
            break;
          }
        }
      }

      if (isNearPoint) {
        canvas.style.cursor = isDragging ? 'grabbing' : 'grab';
        canvas.title = isDragging ? 'Release to drop point' : 'Drag to move point (Right-click to remove)';
      } else if (isNearLine) {
        canvas.style.cursor = 'crosshair';
        canvas.title = 'Click to add point';
      } else {
        canvas.style.cursor = 'default';
        canvas.title = isEditing ? 'Press Delete to remove polygon' : '';
      }
    }
    // Normal polygon drawing mode
    else if (currentData?.activePolygon.length > 2) {
      const firstPoint = currentData.activePolygon[0];
      const distance = Math.hypot(firstPoint.x - coords.x, firstPoint.y - coords.y);
      if (distance < 10) {
        canvas.style.cursor = 'pointer';
        canvas.title = 'Click to close polygon';
      } else {
        canvas.style.cursor = 'crosshair';
        canvas.title = '';
      }
    } else {
      canvas.style.cursor = 'crosshair';
      canvas.title = '';
    }
    
    renderSegmentation();
  }, [segmentationData, imageId, isEditing, selectedPolygon, isDragging, dragPoint, renderSegmentation, setSegmentationData, enableSam, samLoading]);

  // Handle context menu
  const handleContextMenu = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    
    if (enableSam) return;

    if (!segmentationCanvasRef.current || !isEditing || selectedPolygon === null || !imageId) return;
    
    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    const currentData = segmentationData[imageId];
    if (!currentData?.polygons[selectedPolygon]) return;

    // Find if we clicked near any point
    const pointIndex = currentData.polygons[selectedPolygon].points.findIndex((point: Point) => 
      Math.hypot(point.x - coords.x, point.y - coords.y) < VERTEX_HIT_RADIUS
    );

    if (pointIndex !== -1 && currentData.polygons[selectedPolygon].points.length > 3) {
      // Handle point deletion
      setSegmentationData(prev => {
        const currentData = prev[imageId];
        const newPolygons = [...currentData.polygons];
        const updatedPoints = [...newPolygons[selectedPolygon].points];
        updatedPoints.splice(pointIndex, 1);
        newPolygons[selectedPolygon] = {
          ...newPolygons[selectedPolygon],
          points: updatedPoints
        };

        return {
          ...prev,
          [imageId]: {
            ...currentData,
            polygons: newPolygons,
          }
        };
      });
    } else {
      // Show label edit dialog
      setEditingLabel({
        polygonIndex: selectedPolygon,
        position: { x: e.clientX, y: e.clientY }
      });
    }
  };

  // Handle mouse down for dragging and edge point insertion
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (enableSam) return;

    const coords = getCanvasCoordinates(e);
    if (!coords || !isEditing || selectedPolygon === null || !imageId) return;

    const currentData = segmentationData[imageId];
    if (!currentData?.polygons[selectedPolygon]) return;

    const polygonPoints = currentData.polygons[selectedPolygon].points;
    const pointIndex = polygonPoints.findIndex(point =>
      Math.hypot(point.x - coords.x, point.y - coords.y) < VERTEX_HIT_RADIUS
    );

    if (pointIndex !== -1) {
      suppressEditModeExitClickRef.current = true;
      setIsDragging(true);
      setDragPoint({
        index: pointIndex,
        initialPos: polygonPoints[pointIndex]
      });
      return;
    }

    let minDistance = Infinity;
    let insertIndex = -1;
    let nearestPoint: Point | null = null;

    for (let i = 0; i < polygonPoints.length; i++) {
      const start = polygonPoints[i];
      const end = polygonPoints[(i + 1) % polygonPoints.length];
      const { distance, point } = distanceToLineSegment(coords, start, end);

      if (distance < EDGE_HIT_THRESHOLD && distance < minDistance) {
        minDistance = distance;
        insertIndex = i + 1;
        nearestPoint = point;
      }
    }

    if (insertIndex !== -1 && nearestPoint) {
      const newPoint: Point = { x: nearestPoint.x, y: nearestPoint.y };

      setSegmentationData(prev => {
        const data = prev[imageId];
        const newPolygons = [...data.polygons];
        const updatedPoints = [...newPolygons[selectedPolygon].points];
        updatedPoints.splice(insertIndex, 0, newPoint);
        newPolygons[selectedPolygon] = {
          ...newPolygons[selectedPolygon],
          points: updatedPoints
        };

        return {
          ...prev,
          [imageId]: {
            ...data,
            polygons: newPolygons,
          }
        };
      });

      suppressEditModeExitClickRef.current = true;
      setIsDragging(true);
      setDragPoint({
        index: insertIndex,
        initialPos: newPoint
      });
    }
  };

  // Handle mouse up
  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDragging && dragPoint !== null && selectedPolygon !== null && imageId) {
      const coords = getCanvasCoordinates(e);
      if (coords) {
        setSegmentationData(prev => {
          const currentData = prev[imageId];
          const newPolygons = [...currentData.polygons];
          const updatedPoints = [...newPolygons[selectedPolygon].points];
          updatedPoints[dragPoint.index] = coords;
          newPolygons[selectedPolygon] = {
            ...newPolygons[selectedPolygon],
            points: updatedPoints
          };

          return {
            ...prev,
            [imageId]: {
              ...currentData,
              polygons: newPolygons
            }
          };
        });
      }
    }
    
    setIsDragging(false);
    setDragPoint(null);
  };

  // Handle delete key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        
        if (!imageId || enableSam) return;
        
        // Delete polygon in edit mode
        if (isEditing && selectedPolygon !== null) {
          setSegmentationData(prev => {
            const currentData = prev[imageId];
            const newPolygons = [...currentData.polygons];
            newPolygons.splice(selectedPolygon, 1);

            return {
              ...prev,
              [imageId]: {
                ...currentData,
                polygons: newPolygons
              }
            };
          });
          setIsEditing(false);
          setSelectedPolygon(null);
        }
        // Delete last point while drawing polygon
        else if (segmentationData[imageId]?.activePolygon.length > 0) {
          setSegmentationData(prev => {
            const currentData = prev[imageId];
            return {
              ...prev,
              [imageId]: {
                ...currentData,
                activePolygon: currentData.activePolygon.slice(0, -1)
              }
            };
          });
          setIsPolygonClosed(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, selectedPolygon, imageId, segmentationData, setSegmentationData, enableSam]);

  const handleSamLabelQueueSelect = (labelName: string) => {
    if (!imageId || labelQueueIndices.length === 0) return;

    const polygonIndex = labelQueueIndices[labelQueuePosition];
    const labelColor = labels.find((l) => l.name === labelName)?.color || '#FF0000';

    setSegmentationData((prev) => {
      const currentData = prev[imageId];
      const newPolygons = [...currentData.polygons];
      newPolygons[polygonIndex] = {
        ...newPolygons[polygonIndex],
        label: labelName,
        color: labelColor,
      };

      return {
        ...prev,
        [imageId]: {
          ...currentData,
          polygons: newPolygons,
        },
      };
    });

    const nextPosition = labelQueuePosition + 1;
    if (nextPosition >= labelQueueIndices.length) {
      setLabelQueueIndices([]);
      setLabelQueuePosition(0);
    } else {
      setLabelQueuePosition(nextPosition);
    }
  };

  // Handle label selection
  const handleLabelSelect = (label: string) => {
    if (pendingPolygon && imageId) {
      const newPolygon: Polygon = {
        points: pendingPolygon,
        label: label,
        color: labels.find(l => l.name === label)?.color || '#FF0000'
      };

      setSegmentationData(prev => ({
        ...prev,
        [imageId]: {
          ...prev[imageId],
          polygons: [...prev[imageId].polygons, newPolygon],
          activePolygon: []
        }
      }));

      setPendingPolygon(null);
      setIsPolygonClosed(false);
    }
    setActiveLabel(label);
  };

  // Save segmentation - removed since it's now in the main page
  // const saveSegmentation = async () => { ... }

  const SamLabelQueueDialog = () => {
    if (labelQueueIndices.length === 0 || !imageId) return null;

    const maskNumber = labelQueuePosition + 1;
    const totalMasks = labelQueueIndices.length;

    return (
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          p: 2,
          zIndex: 1000,
          backgroundColor: 'white',
          boxShadow: 3,
        }}
      >
        <Typography gutterBottom>
          Select a label for SAM mask {maskNumber} of {totalMasks}:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {labels.map((label, index) => (
            <Button
              key={`${label.name}-${index}`}
              onClick={() => handleSamLabelQueueSelect(label.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'flex-start',
              }}
            >
              <div
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: label.color,
                  borderRadius: '50%',
                }}
              />
              {label.name}
            </Button>
          ))}
        </Box>
      </Paper>
    );
  };

  // Label selection dialog
  const LabelSelectionDialog = () => {
    if (!pendingPolygon) return null;

    return (
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          p: 2,
          zIndex: 1000,
          backgroundColor: 'white',
          boxShadow: 3
        }}
      >
        <Typography gutterBottom>Select a label for the polygon:</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {labels.map((label, index) => (
            <Button
              key={`${label.name}-${index}`}
              onClick={() => handleLabelSelect(label.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'flex-start'
              }}
            >
              <div style={{
                width: 16,
                height: 16,
                backgroundColor: label.color,
                borderRadius: '50%'
              }} />
              {label.name}
            </Button>
          ))}
        </Box>
      </Paper>
    );
  };

  // Label edit dialog
  const LabelEditDialog = memo(() => {
    if (!editingLabel || !imageId) return null;

    return (
      <Paper
        ref={dialogRef}
        sx={{
          position: 'fixed',
          left: editingLabel.position.x,
          top: editingLabel.position.y,
          p: 2,
          zIndex: 1000,
          backgroundColor: 'white',
          boxShadow: 3
        }}
      >
        <Typography gutterBottom>Change label:</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {labels.map((label, index) => (
            <Button
              key={`${label.name}-${index}`}
              onClick={() => {
                setSegmentationData(prev => {
                  const currentData = prev[imageId];
                  const newPolygons = [...currentData.polygons];
                  newPolygons[editingLabel.polygonIndex] = {
                    ...newPolygons[editingLabel.polygonIndex],
                    label: label.name,
                    color: label.color
                  };

                  return {
                    ...prev,
                    [imageId]: {
                      ...currentData,
                      polygons: newPolygons
                    }
                  };
                });
                setEditingLabel(null);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'flex-start'
              }}
            >
              <div style={{
                width: 16,
                height: 16,
                backgroundColor: label.color,
                borderRadius: '50%'
              }} />
              {label.name}
            </Button>
          ))}
        </Box>
      </Paper>
    );
  });

  // Handle click outside dialog
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing && 
          segmentationCanvasRef.current && 
          !segmentationCanvasRef.current.contains(event.target as Node) &&
          (!dialogRef.current || !dialogRef.current.contains(event.target as Node))) {
        setIsEditing(false);
        setSelectedPolygon(null);
        setEditingLabel(null);
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isEditing]);

  if (error) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!imageLoaded) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading image...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {imageData && (
        <ViewerContainer elevation={0}>
          <ControlsContainer>
            <Typography variant="h6">File Name - {imageData.id}</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={enableSam}
                  onChange={handleSamToggle}
                  disabled={!projectId || samToggleLoading}
                  color="primary"
                />
              }
              label="Enable SAM"
            />
          </ControlsContainer>

          <ViewerWrapper>
            <CanvasContainer>
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              >
              <canvas
                ref={canvasRef}
                style={{
                  display: 'block',
                  zIndex: 1
                }}
              />
              <canvas
                ref={segmentationCanvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: samLoading ? 'none' : 'all',
                  zIndex: 2
                }}
                onClick={handleCanvasClick}
                onContextMenu={handleContextMenu}
                onMouseDown={handleMouseDown}
                onMouseMove={handleSegmentationCanvasMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
              {samLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.4)',
                    zIndex: 3,
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              )}
              </Box>
            </CanvasContainer>
          </ViewerWrapper>
          
          <LabelSelectionDialog />
          <SamLabelQueueDialog />
          <LabelEditDialog />

          <Dialog
            open={samInitDialogOpen}
            onClose={(_, reason) => {
              if (reason === 'backdropClick') return;
              handleSamInitDismiss();
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Initializing SAM</DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {samInitMessage}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                We are initializing SAM. Please wait.
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, samInitPercentage))}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {Math.round(samInitPercentage)}% complete
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleSamInitDismiss} color="primary">
                Okay, come back later
              </Button>
            </DialogActions>
          </Dialog>
        </ViewerContainer>
      )}

      {!imageData && (
        <WelcomeContainer>
          <WelcomeContent>
            <WelcomeIcon />
            <WelcomeTitle variant="h4">Welcome to Image Segmentation</WelcomeTitle>
            <WelcomeText variant="body1">
              Select an image from the list on the left to start labeling. 
              You can draw polygons and assign labels to different regions in the image.
            </WelcomeText>
          </WelcomeContent>
        </WelcomeContainer>
      )}
    </Box>
  );
}

export default ImageSegmentationViewer; 